import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

/**
 * Hook to manage variant links
 */
export const useVariantLinks = (productId) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLinks = async () => {
    if (!productId) {
      setLinks([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('product_variant_links')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setLinks(data || []);
    } catch (err) {
      console.error('Error fetching variant links:', err);
      setError(err.message);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [productId]);

  const createLink = async (sourceVariantId, targetVariantId, linkType = 'variant_link') => {
    try {
      const { data, error: insertError } = await supabase
        .from('product_variant_links')
        .insert({
          product_id: productId,
          source_variant_id: sourceVariantId,
          target_variant_id: targetVariantId,
          link_type: linkType
        })
        .select()
        .single();

      if (insertError) throw insertError;
      await fetchLinks();
      return { data, error: null };
    } catch (err) {
      console.error('Error creating link:', err);
      return { data: null, error: err };
    }
  };

  const createBulkLinks = async (sourceVariantId, targetVariantIds, linkType = 'variant_link') => {
    try {
      const inserts = targetVariantIds.map(targetId => ({
        product_id: productId,
        source_variant_id: sourceVariantId,
        target_variant_id: targetId,
        link_type: linkType
      }));

      const { data, error: insertError } = await supabase
        .from('product_variant_links')
        .insert(inserts)
        .select();

      if (insertError) throw insertError;
      await fetchLinks();
      return { data, error: null };
    } catch (err) {
      console.error('Error creating bulk links:', err);
      return { data: null, error: err };
    }
  };

  const deleteLink = async (linkId) => {
    try {
      const { error: deleteError } = await supabase
        .from('product_variant_links')
        .delete()
        .eq('id', linkId);

      if (deleteError) throw deleteError;
      await fetchLinks();
      return { error: null };
    } catch (err) {
      console.error('Error deleting link:', err);
      return { error: err };
    }
  };

  const deleteLinksBySource = async (sourceVariantId) => {
    try {
      const { error: deleteError } = await supabase
        .from('product_variant_links')
        .delete()
        .eq('source_variant_id', sourceVariantId)
        .eq('product_id', productId);

      if (deleteError) throw deleteError;
      await fetchLinks();
      return { error: null };
    } catch (err) {
      console.error('Error deleting links by source:', err);
      return { error: err };
    }
  };

  const deleteLinksByTarget = async (targetVariantId) => {
    try {
      const { error: deleteError } = await supabase
        .from('product_variant_links')
        .delete()
        .eq('target_variant_id', targetVariantId)
        .eq('product_id', productId);

      if (deleteError) throw deleteError;
      await fetchLinks();
      return { error: null };
    } catch (err) {
      console.error('Error deleting links by target:', err);
      return { error: err };
    }
  };

  // Get all variants linked from a source
  const getLinkedTargets = (sourceVariantId) => {
    return links.filter(link => link.source_variant_id === sourceVariantId);
  };

  // Get all variants linking to a target
  const getLinkingSources = (targetVariantId) => {
    return links.filter(link => link.target_variant_id === targetVariantId);
  };

  return {
    links,
    loading,
    error,
    fetchLinks,
    createLink,
    createBulkLinks,
    deleteLink,
    deleteLinksBySource,
    deleteLinksByTarget,
    getLinkedTargets,
    getLinkingSources
  };
};

